import { QuestionnaireQuestion } from "@repo/api";
import { useCallback } from "react";
import { FieldErrors, Resolver } from "react-hook-form";
import { useTypedTranslation } from "../../../hooks";
import { QuestionnaireFormValues } from "./QuestionnairePage.helpers";
import { useLinkAnswersWithApiQuestions } from "./useLinkAnswersWithApiQuestions";

export const useQuestionnaireFormValidator = (
    data: QuestionnaireQuestion[] | undefined,
): Resolver<QuestionnaireFormValues, any, QuestionnaireFormValues> => {
    const { t } = useTypedTranslation();

    const link = useLinkAnswersWithApiQuestions(data);

    const resolver = useCallback(
        (values: QuestionnaireFormValues) => {
            const errors: FieldErrors<QuestionnaireFormValues> = {};

            const linked = link(values);

            linked.forEach(({ questionId, apiQuestion, userAnswers }) => {
                if (apiQuestion?.multipleAllowed === true) {
                    //checkboxes check
                    const checkedAnswers = userAnswers.filter((a) => !!a.answer);

                    if (checkedAnswers.length === 0) {
                        // no answer selected
                        errors[questionId] = {
                            type: "required",
                            message: t(
                                "investmentStrategy.pensionQuestionnaire.questionnairePage.errors.requiredAnswer",
                            ),
                        };
                    }

                    if (checkedAnswers.length > 1 && checkedAnswers.some((a) => a.apiAnswer?.excludeOthers)) {
                        // if any answer excludes others, ensure only one is checked
                        errors[questionId] = {
                            type: "exclusive",
                            message: t(
                                "investmentStrategy.pensionQuestionnaire.questionnairePage.errors.excludedAnswer",
                            ),
                        };
                    }
                } else if (userAnswers.length === 0) {
                    //radio buttons check
                    errors[questionId] = {
                        type: "required",
                        message: t("investmentStrategy.pensionQuestionnaire.questionnairePage.errors.requiredAnswer"),
                    };
                }
            });

            if (Object.keys(errors).length > 0) {
                return { values: {}, errors };
            } else {
                return { values, errors: {} };
            }
        },
        [link, t],
    );

    return resolver;
};
